import moment from "moment";
import "moment/locale/bs";
import IModuleContainer from "./runtime/IModuleContainer";
import ModuleContainer from "./runtime/ModuleContainer";
import { IStore } from "./model/runtime/state/Store";
import { IRouter, Router } from "./runtime/Router";
import { IArea } from "./areas/Area";
import { HttpConstants, Errors } from "./common/Constants";
import ShowErrorMessageInteractor from "./interactor/notifications/ShowErrorMessageInteractor";
import ShowWarningMessageInteractor from "./interactor/notifications/ShowWarningMessageInteractor";
import LogoutInteractor from "./interactor/authentication/LogoutInteractor";
import ApplicationConfig from "./ApplicationConfig";
import { IAsyncLocaleProvider } from "./service/locale/AsyncLocaleProvider";
import GetSelectedLanguageInteractor from "./interactor/settings/GetSelectedLanguageInteractor";
import ShowUnauthorizedMsgInteractor from "./interactor/authentication/ShowUnauthorizedMsgInteractor";

export default class Application {
  private _router: IRouter;
  private _container: IModuleContainer;
  private _localeProvider: IAsyncLocaleProvider;
  private _appConfig: ApplicationConfig;

  private _applicationHome = "/home";

  get container() {
    return this._container;
  }

  get navigator() {
    return this._router;
  }

  get localeProvider() {
    return this._localeProvider;
  }

  get applicationHome() {
    return this._applicationHome;
  }

  get applicationName() {
    return this._appConfig.getApplicationName();
  }

  constructor(appName: string, context: any, rootElement: HTMLElement) {
    this._appConfig = new ApplicationConfig(appName);
    this._container = ModuleContainer(this);
    this._router = Router(
      context,
      rootElement,
      this,
      [],
      this._appConfig.layouts
    );
    this._container.registerValue("router", this._router);
    this._localeProvider =
      this.container.resolve<IAsyncLocaleProvider>("localeProvider");

    moment.locale("bs");
    window["__secretApplication"] = this;
  }

  private showErrorMessage(message: string) {
    return this.container
      .resolve<ShowErrorMessageInteractor>("showErrorMessage")
      .execute(message);
  }

  private showWarningMessage(message: string) {
    return this.container
      .resolve<ShowWarningMessageInteractor>("showWarningMessage")
      .execute(message);
  }

  private async handleNetworkError(error: any) {
    if (error.status != null) {
      if (error.code === Errors.DB_ERROR_CODE) {
        throw error;
      } else if (error.code === HttpConstants.HTTP_CLIENT_ERROR_CODE) {
        if ((error as any).message && (error as any).message !== "") {
          this.showWarningMessage((error as any).message);
        }
        if (error.status === Errors.UNAUTHORIZED_STATUS) {
          this.container
            .resolve<ShowUnauthorizedMsgInteractor>("showUnauthorizedMsg")
            .execute();
          return window.setTimeout(
            () => this.container.resolve<LogoutInteractor>("logout").execute(),
            2000
          );
        }
        if (error.status === Errors.FORBIDDEN_STATUS) {
          throw error;
        }
      } else {
        //TODO handle 5xx errors
        this.showErrorMessage((error as any).message);
        throw error;
        //TODO log to crashlytics
      }
      return;
    }
    throw error;
  }

  private registerGlobalErrorHandler() {
    window.addEventListener("unhandledrejection", function (e) {
      var reason = e.reason ? e.reason : e.type;

      if (window["Raven"]) {
        e.preventDefault();
        window["Raven"].captureException(new Error(reason));
      }
    });
  }
  public changeSystemLanguage(languageCode: number) {
    this._container.registerValue("language", languageCode);
  }

  private registerAreas(areas: string[]) {
    areas.forEach((area) => {
      this._container.resolve<IArea>(`${area}Area`).register();
    });
  }

  public async onApplicationError(error: any) {
    try {
      await this.handleNetworkError(error);
    } catch (error) {
      throw error;
    }
  }

  async start() {
    let currentLanguage = await this.container
      .resolve<GetSelectedLanguageInteractor>("getSelectedLanguage")
      .execute();
    if (!currentLanguage) {
      currentLanguage = this._container.resolve<number>("language");
    }
    const translations = await this.localeProvider.fetch(currentLanguage);
    this.localeProvider.addLocale(currentLanguage, translations);
    this.localeProvider.changeCurrentLanguage(currentLanguage);

    this.registerAreas(this._appConfig.areas);
    this._router.bindLocationListener();
    this._router.showLocation();

    this.registerGlobalErrorHandler();
  }

  public get store() {
    return this._container.resolve<IStore>("store");
  }

  addLocale(locale: number, translations = {}) {
    if (locale) {
      this.localeProvider.addLocale(locale, translations);
    }
  }
}
